import { Link } from 'react-router-dom';
import Flag from 'react-world-flags';
import { 
  FaAward, 
  FaUsers, 
  FaClock, 
  FaGraduationCap,
  FaDownload,
  FaPlayCircle
} from 'react-icons/fa';

const LearnLanguage = () => {
  const languages = [
    {
      title: 'French',
      description: 'Learn the Language of Art & Diplomacy. Master DELF A1 to DALF C2 with immersive speaking labs and cultural immersion.',
      flagCode: 'FR',
      path: '/french',
      certification: 'DELF/DALF',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      title: 'German',
      description: 'The Key to Europe\'s Strongest Economy. Prepare for Goethe Zertifikat with business vocabulary and cultural intelligence.',
      flagCode: 'DE',
      path: '/german',
      certification: 'Goethe-Zertifikat',
      color: 'from-purple-500 to-pink-500'
    },
    {
      title: 'Spanish',
      description: 'Connect with 20+ Nations. Dive into DELE success strategies, conversation cafés, and multimedia storytelling projects.',
      flagCode: 'ES',
      path: '/spanish',
      certification: 'DELE',
      color: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Japanese',
      description: 'Unlock Career Opportunities in Tech. Strengthen kanji, listening, and cultural etiquette for JLPT N5 to N1.',
      flagCode: 'JP',
      path: '/japanese',
      certification: 'JLPT',
      color: 'from-orange-500 to-red-500'
    },
  ];

  const benefits = [
    { 
      name: 'Structured Learning', 
      icon: <FaGraduationCap />, 
      desc: 'CEFR-aligned curriculum from A1 to C2 levels' 
    },
    { 
      name: 'Small Classes', 
      icon: <FaUsers />, 
      desc: '8-12 students per batch for personalized attention' 
    },
    { 
      name: 'Flexible Schedule', 
      icon: <FaClock />, 
      desc: 'Weekday and weekend batches available' 
    },
    { 
      name: 'Global Recognition', 
      icon: <FaAward />, 
      desc: 'Internationally recognized certifications' 
    },
  ];

  return (
    <div className="min-h-screen bg-canvas pb-20 text-ink">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-primary/10 bg-gradient-to-br from-primary/10 via-white to-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(79,64,198,0.1),transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_left,rgba(79,64,198,0.08),transparent_50%)]" />
        <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-6 px-4 py-20 text-center lg:px-6">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/20 to-primary-soft/20 rounded-full blur-2xl animate-pulse" />
            <h1 className="relative text-5xl font-semibold sm:text-6xl bg-gradient-to-r from-primary to-primary-soft bg-clip-text text-transparent pb-2">
              Learn Language
            </h1>
          </div>
          <p className="max-w-3xl text-lg leading-relaxed text-ink-muted sm:text-xl">
            Explore dynamic programs across global languages with structured levels, 
            cultural immersions, and certification-readiness.
          </p>
        </div>
      </section>

      {/* Languages Grid */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="text-center mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              Our Languages
            </span>
            <h2 className="mt-4 text-3xl font-semibold sm:text-4xl">Comprehensive Language Programs</h2>
            <p className="mt-4 max-w-2xl mx-auto text-ink-muted">
              From basics to proficiency, we provide structured learning paths for all your language goals
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-2">
            {languages.map((language, index) => (
              <Link
                key={language.title}
                to={language.path}
                className="group relative rounded-2xl border border-primary/10 bg-white p-6 shadow-elevated transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_30px_60px_rgba(18,10,56,0.18)] overflow-hidden"
              >
                <div className="relative">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="flex-shrink-0">
                      <div className="relative">
                        <div className="absolute inset-0 rounded-lg bg-primary/20 blur-xl group-hover:blur-2xl transition-all" />
                        <div className="relative flex h-16 w-24 items-center justify-center rounded-lg bg-white shadow-lg overflow-hidden border border-primary/10">
                          <Flag code={language.flagCode} className="h-full w-full object-cover" />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-semibold text-ink group-hover:text-primary transition-colors">{language.title}</h3>
                        <span className="px-2 py-1 rounded-full bg-primary/10 text-xs font-medium text-primary">
                          {language.certification}
                        </span>
                      </div>
                      <p className="text-sm leading-relaxed text-ink-muted">{language.description}</p>
                    </div>
                  </div>
                  <div className="mt-4 text-sm font-medium text-primary opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2">
                    Learn more →
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-canvas">
        <div className="mx-auto max-w-6xl px-4 lg:px-6">
          <div className="text-center mb-12">
            <span className="inline-flex rounded-full bg-primary/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.25em] text-primary">
              Why Choose Us
            </span>
            <h2 className="mt-4 text-3xl font-semibold sm:text-4xl">What Makes Us Different</h2>
            <p className="mt-4 max-w-2xl mx-auto text-ink-muted">
              Our language programs are designed to help you achieve fluency and certification success
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit, index) => (
              <div
                key={benefit.name}
                className="group rounded-2xl border border-primary/10 bg-white p-6 shadow-elevated transition-all duration-300 hover:-translate-y-1 hover:shadow-float"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/10 to-primary-soft/10 text-xl text-primary mb-4 group-hover:scale-110 transition-transform">
                  {benefit.icon}
                </div>
                <h3 className="font-semibold text-ink mb-2">{benefit.name}</h3>
                <p className="text-sm text-ink-muted">{benefit.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-4xl px-4 lg:px-6">
          <div className="rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/5 via-white to-white/80 p-8 shadow-[0_28px_60px_rgba(15,23,42,0.18)]">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-semibold mb-4">Ready to Start Your Language Journey?</h2>
              <p className="text-ink-muted">
                Get in touch with our advisors to find the perfect course and schedule for your goals
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-primary to-primary-soft px-8 py-3 text-sm font-semibold uppercase tracking-wide text-white shadow-[0_18px_30px_rgba(79,64,198,0.35)] transition hover:-translate-y-1 hover:shadow-[0_24px_40px_rgba(18,10,56,0.35)]"
              >
                <FaDownload />
                Download Brochure
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-primary px-8 py-3 text-sm font-semibold uppercase tracking-wide text-primary transition hover:-translate-y-1 hover:bg-primary/5"
              >
                <FaPlayCircle />
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LearnLanguage;
